import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, MessageSquare, BookOpen, CreditCard, Users, Star, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PasswordInput from '@/components/PasswordInput';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL as API } from '@/utils/apiConfig';


const LOSS_ICONS = [MessageSquare, BookOpen, CreditCard, Users, Star, Database];
const LOSS_KEYS = ['loss_conversations', 'loss_bookmarks', 'loss_subscription', 'loss_share', 'loss_settings', 'loss_data'];
const REASON_KEYS = [
  { value: 'too_expensive', key: 'reason_expensive' },
  { value: 'not_useful', key: 'reason_not_useful' },
  { value: 'not_accurate_enough', key: 'reason_not_accurate' },
  { value: 'found_alternative', key: 'reason_alternative' },
  { value: 'privacy_concerns', key: 'reason_privacy' },
  { value: 'technical_issues', key: 'reason_technical' },
  { value: 'taking_a_break', key: 'reason_break' },
  { value: 'other', key: 'reason_other' },
];

export default function DeleteAccountModal({ onClose, authMethod }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const needsPassword = authMethod === 'email';
  const canDelete = needsPassword ? password.length >= 4 : confirmText === 'DELETE';

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/delete-account`, {
        reason,
        reason_detail: reasonDetail,
        password: needsPassword ? password : '',
      }, { withCredentials: true });
      toast.success(t('delete_account.success'));
      localStorage.clear();
      navigate('/login');
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('delete_account.failed'));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="delete-account-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-md rounded-3xl p-6 shadow-2xl animate-in zoom-in-95 fade-in duration-300 overflow-y-auto"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, rgba(51,65,85,0.4))',
          maxHeight: 'min(85vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        {step === 1 && (
          <>
            <div className="text-center mb-5">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(239, 68, 68, 0.12)' }}
              >
                <AlertTriangle className="h-7 w-7 text-red-400" />
              </div>
              <h2 className="text-lg font-bold" style={{ color: 'var(--theme-text)' }} data-testid="delete-modal-title">
                {t('delete_account.title')}
              </h2>
              <p className="text-sm mt-2" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('delete_account.warning')}
              </p>
            </div>

            <div className="space-y-2.5 mb-5" data-testid="delete-losses-list">
              {LOSS_KEYS.map((key, i) => {
                const Icon = LOSS_ICONS[i];
                return (
                  <div
                    key={key}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                    style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)' }}
                  >
                    <Icon className="h-4 w-4 text-red-400 shrink-0" />
                    <span className="text-sm" style={{ color: 'var(--theme-text)' }}>{t(`delete_account.${key}`)}</span>
                  </div>
                );
              })}
            </div>

            <div
              className="rounded-xl px-3 py-2.5 mb-5 text-xs"
              style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)', color: 'var(--theme-textSecondary)' }}
              dangerouslySetInnerHTML={{ __html: t('delete_account.reactivation_notice') }}
            />

            <div className="flex gap-3">
              <Button
                onClick={onClose}
                variant="ghost"
                className="flex-1 rounded-xl"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid="delete-cancel-btn"
              >
                {t('delete_account.keep_account')}
              </Button>
              <Button
                onClick={() => setStep(2)}
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-500 text-white"
                data-testid="delete-continue-btn"
              >
                {t('delete_account.continue')}
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-base font-bold mb-4" style={{ color: 'var(--theme-text)' }}>
              {t('delete_account.before_you_go')}
            </h2>

            <div className="mb-4">
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('delete_account.why_leaving')}
              </label>
              <select
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none appearance-none"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                data-testid="delete-reason-select"
              >
                <option value="">{t('delete_account.select_reason')}</option>
                {REASON_KEYS.map(r => (
                  <option key={r.value} value={r.value}>{t(`delete_account.${r.key}`)}</option>
                ))}
              </select>
            </div>

            {reason === 'other' && (
              <div className="mb-4">
                <textarea
                  value={reasonDetail}
                  onChange={e => setReasonDetail(e.target.value)}
                  placeholder={t('delete_account.tell_us_more')}
                  rows={2}
                  className="w-full rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="delete-reason-detail"
                />
              </div>
            )}

            <div className="mb-5">
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--theme-textSecondary)' }}>
                {needsPassword ? t('delete_account.enter_password') : t('delete_account.type_delete')}
              </label>
              {needsPassword ? (
                <PasswordInput
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('delete_account.your_password')}
                  className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="delete-password-input"
                />
              ) : (
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={t('delete_account.type_delete_placeholder')}
                  className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none font-mono tracking-wider"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="delete-confirm-input"
                />
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="ghost"
                className="flex-1 rounded-xl"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid="delete-back-btn"
              >
                {t('delete_account.back')}
              </Button>
              <Button
                onClick={handleDelete}
                disabled={loading || !canDelete}
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 gap-2"
                data-testid="delete-confirm-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('delete_account.delete_permanently')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
